import { Check, Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type {
  FileUploadStatus,
  IngestionProgress,
} from '@/shared/hooks/api/productSource'
import { useRetryProductSourceIngestion } from '@/shared/hooks/api/productSource'
import {
  useReuploadProductSourceFile,
  type ReuploadProductSourceFileControls,
} from '@/features/product-catalog/hooks/useReuploadProductSourceFile'
import { useIngestionDisplayPercent } from '@/features/product-catalog/hooks/useIngestionDisplayPercent'
import { PRODUCT_SOURCE_FILE_ACCEPT } from '@/features/product-catalog/utils/productSourceFileTypes'
import { getErrorAction } from '@/features/product-catalog/utils/errorActions'
import {
  getEffectiveIngestionErrorCode,
  getIngestionFailureMessage,
} from '@/features/product-catalog/utils/ingestionFailureMessage'
import {
  isProductSourceUploadFailed,
  isProductSourceUploadIncomplete,
} from '@/features/product-catalog/utils/productSourceUploadStatus'
import { toMachineStageKey } from '@/features/product-catalog/utils/toMachineStageKey'

type Status = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED'

export function ProductCatalogIngestionStatus({
  productId,
  sourceId,
  status,
  fileUploadStatus,
  filePathInBucket = '',
  isUploading,
  stage,
  progress,
  ingestionErrorCode,
  sourceContentType,
  sourceFileName,
  partialResult,
  missingPages,
  reuploadControls,
  onReuploadClick,
}: {
  productId?: string
  sourceId: string
  status?: Status
  fileUploadStatus?: FileUploadStatus
  filePathInBucket?: string
  isUploading?: boolean
  stage?: string | null
  progress?: IngestionProgress | null
  ingestionErrorCode?: string | null
  sourceContentType?: string | null
  sourceFileName?: string | null
  partialResult?: boolean | null
  missingPages?: string[]
  reuploadControls?: ReuploadProductSourceFileControls
  onReuploadClick?: () => void
}) {
  const { t } = useTranslation('productCatalog')
  const retryIngestion = useRetryProductSourceIngestion(productId ?? '')
  const internalReupload = useReuploadProductSourceFile(productId ?? '')
  const reupload = reuploadControls ?? internalReupload
  const ownsFileInput = reuploadControls == null

  const handleReuploadClick = () => {
    reupload.openFilePickerForSource(sourceId)
    onReuploadClick?.()
  }

  const resolvedUploadStatus = fileUploadStatus ?? 'PENDING'
  const uploadIncomplete = isProductSourceUploadIncomplete({
    fileUploadStatus: resolvedUploadStatus,
    filePathInBucket,
  })

  const showClientUploading =
    isUploading === true || reupload.isReuploading(sourceId)

  const shownPercent = useIngestionDisplayPercent({
    sourceId,
    status,
    fileUploadStatus: resolvedUploadStatus,
    filePathInBucket,
    isUploading: showClientUploading,
    progress,
  })

  const fileInput = ownsFileInput ? (
    <input
      ref={reupload.fileInputRef}
      type='file'
      accept={PRODUCT_SOURCE_FILE_ACCEPT}
      className='hidden'
      onChange={reupload.handleFileInputChange}
    />
  ) : null

  if (showClientUploading) {
    return (
      <>
        {fileInput}
        <span className='text-xs text-amber-600'>
          {t('sources.upload.uploading')}
        </span>
      </>
    )
  }

  if (uploadIncomplete) {
    const canReupload = productId != null && productId.length > 0
    return (
      <>
        {fileInput}
        <div className='flex flex-wrap items-center gap-2'>
          <span className='text-xs text-amber-600'>
            {t('sources.status.uploadIncomplete')}
          </span>
          {canReupload && (
            <Button
              type='button'
              size='sm'
              variant='outline'
              className='h-7 px-2 text-xs'
              onClick={handleReuploadClick}
            >
              <Upload className='size-3.5' aria-hidden />
              {t('sources.status.reupload')}
            </Button>
          )}
        </div>
      </>
    )
  }

  const uploadFailed = isProductSourceUploadFailed(resolvedUploadStatus)

  if (status === 'FAILED' || uploadFailed) {
    const sourceContext = {
      sourceContentType,
      sourceFileName,
    }
    const failureMessage = uploadFailed
      ? t('sources.status.uploadFailed')
      : getIngestionFailureMessage(
          ingestionErrorCode,
          (key, defaultValue) => t(`sources.${key}`, { defaultValue }),
          sourceContext
        )
    const effectiveErrorCode = getEffectiveIngestionErrorCode(
      ingestionErrorCode,
      sourceContext
    )
    const errorAction = uploadFailed
      ? null
      : getErrorAction(effectiveErrorCode)
    const ErrorActionIcon = errorAction?.icon
    const canRetry =
      errorAction?.action === 'retry' &&
      productId != null &&
      productId.length > 0
    const canReupload =
      uploadFailed && productId != null && productId.length > 0

    const handleRetryClick = () => {
      if (!canRetry) {
        return
      }
      void retryIngestion.mutateAsync({ sourceId })
    }

    return (
      <>
        {fileInput}
        <div className='flex flex-wrap items-center gap-2'>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className='cursor-default text-xs text-destructive underline decoration-dotted underline-offset-2'
                aria-label={failureMessage}
              >
                {t('sources.status.failed')}
              </span>
            </TooltipTrigger>
            <TooltipContent className='max-w-xs text-left'>
              {failureMessage}
            </TooltipContent>
          </Tooltip>
          {canReupload && (
            <Button
              type='button'
              size='sm'
              variant='outline'
              className='h-7 px-2 text-xs'
              disabled={reupload.isReuploading(sourceId)}
              onClick={handleReuploadClick}
            >
              <Upload className='size-3.5' aria-hidden />
              {t('sources.status.reupload')}
            </Button>
          )}
          {errorAction != null && ErrorActionIcon != null && (
            <Button
              type='button'
              size='sm'
              variant='outline'
              className='h-7 px-2 text-xs'
              disabled={!canRetry || retryIngestion.isPending}
              onClick={handleRetryClick}
            >
              <ErrorActionIcon className='size-3.5' aria-hidden />
              {t(errorAction.labelKey)}
            </Button>
          )}
        </div>
      </>
    )
  }
  if (status === 'COMPLETED' && (partialResult === true || (missingPages?.length ?? 0) > 0)) {
    const missingCount = missingPages?.length ?? 0
    const partialTooltip =
      missingCount === 0
        ? t('sources.status.partialResultUnknownPages')
        : t('sources.status.partialResult', { count: missingCount })

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='inline-flex items-center gap-1 text-xs text-amber-600'>
            <Check className='size-3.5' aria-hidden />
            <span>{t('sources.status.partial')}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent className='max-w-xs text-left'>
          {partialTooltip}
        </TooltipContent>
      </Tooltip>
    )
  }
  if (status === 'COMPLETED') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className='inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700'
            aria-label={t('sources.status.ingestionCompleted')}
          >
            <Check className='size-3.5' />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {t('sources.status.ingestionCompleted')}
        </TooltipContent>
      </Tooltip>
    )
  }
  if (status === 'PENDING' || status === 'PROCESSING') {
    const machineStageKey = toMachineStageKey(stage)
    const stageLabel =
      machineStageKey != null
        ? t(`sources.status.stages.${machineStageKey}`, t('sources.status.processing'))
        : t('sources.status.processing')
    const label =
      shownPercent != null ? `${stageLabel}… ${shownPercent}%` : `${stageLabel}…`
    return (
      <span className='text-xs text-muted-foreground' title={label}>
        {label}
      </span>
    )
  }
  return null
}
